/* ============================================
   MarketingDashboard — Marketing Metrics Dashboard
   KPIs, Funnel, Campaigns & Bookings
   ============================================ */

import { useNavigate } from 'react-router-dom'
import { useApi } from '@/hooks/useApi'
import { useCachedData } from '@/hooks/useCachedData'
import type { CampanaEmail, Agendamiento, NewsletterSuscriptor } from '@/lib/types'
import { PageHeader } from '@/components/ui'

// ─── Skeleton pulse animation ───
const skeletonKeyframes = `
@keyframes skeletonPulse {
  0% { opacity: 0.4; }
  50% { opacity: 0.8; }
  100% { opacity: 0.4; }
}
`

// ─── Styles ───
const styles = {
  page: {
    padding: '32px 40px',
    minHeight: '100vh',
  } as React.CSSProperties,

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 32,
  } as React.CSSProperties,

  title: {
    fontSize: 26,
    fontWeight: 700,
    color: 'var(--text-primary)',
    margin: 0,
  } as React.CSSProperties,

  actions: {
    display: 'flex',
    gap: 12,
  } as React.CSSProperties,

  btnPrimary: {
    background: 'var(--brand-500)',
    color: 'var(--text-on-primary)',
    padding: '12px 24px',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 14,
    border: 'none',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  } as React.CSSProperties,

  btnSecondary: {
    background: 'var(--veil-08)',
    color: 'var(--text-primary)',
    padding: '12px 24px',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 14,
    border: '1px solid var(--border-light)',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  } as React.CSSProperties,

  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 20,
    marginBottom: 32,
  } as React.CSSProperties,

  card: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: 20,
  } as React.CSSProperties,

  kpiValue: {
    fontSize: 32,
    fontWeight: 700,
    color: 'var(--primary-light)',
    margin: 0,
    lineHeight: 1.2,
  } as React.CSSProperties,

  kpiLabel: {
    fontSize: 13,
    color: 'var(--text-muted)',
    margin: 0,
    marginTop: 6,
  } as React.CSSProperties,

  sectionTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: '0 0 16px 0',
  } as React.CSSProperties,

  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 24,
  } as React.CSSProperties,

  funnelBar: {
    height: 32,
    borderRadius: 8,
    background: 'linear-gradient(90deg, #4F7FE8, #342CDE)',
    transition: 'width 0.6s ease',
  } as React.CSSProperties,

  funnelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 12,
  } as React.CSSProperties,

  funnelLabel: {
    fontSize: 13,
    color: 'var(--text-secondary)',
    width: 90,
    textAlign: 'right' as const,
    flexShrink: 0,
  } as React.CSSProperties,

  funnelTrack: {
    flex: 1,
    height: 32,
    borderRadius: 8,
    background: 'var(--veil-06)',
    overflow: 'hidden',
  } as React.CSSProperties,

  funnelCount: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--primary-light)',
    width: 60,
    textAlign: 'right' as const,
    flexShrink: 0,
  } as React.CSSProperties,

  progressTrack: {
    height: 6,
    borderRadius: 3,
    background: 'var(--veil-10)',
    overflow: 'hidden',
    marginTop: 10,
  } as React.CSSProperties,

  progressFill: {
    height: 6,
    borderRadius: 3,
    background: 'linear-gradient(90deg, #4F7FE8, #342CDE)',
    transition: 'width 0.5s ease',
  } as React.CSSProperties,

  campaignItem: {
    padding: '14px 0',
    borderBottom: '1px solid var(--border)',
  } as React.CSSProperties,

  campaignHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  } as React.CSSProperties,

  campaignName: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--text-primary)',
  } as React.CSSProperties,

  campaignStats: {
    fontSize: 12,
    color: 'var(--text-muted)',
    marginTop: 4,
  } as React.CSSProperties,

  bookingItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: '1px solid var(--border)',
  } as React.CSSProperties,

  bookingName: {
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--text-primary)',
  } as React.CSSProperties,

  bookingMeta: {
    fontSize: 12,
    color: 'var(--text-muted)',
    marginTop: 2,
  } as React.CSSProperties,

  bookingTime: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--primary-light)',
    textAlign: 'right' as const,
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,

  skeleton: {
    background: 'var(--veil-06)',
    borderRadius: 8,
    animation: 'skeletonPulse 1.5s ease-in-out infinite',
  } as React.CSSProperties,

  emptyState: {
    padding: '32px 0',
    textAlign: 'center' as const,
    color: 'var(--text-muted)',
    fontSize: 14,
  } as React.CSSProperties,
} as const

// ─── Status badge styles ───
const badgeStyles: Record<string, React.CSSProperties> = {
  borrador: {
    padding: '2px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    background: 'var(--veil-10)',
    color: 'var(--text-muted)',
    display: 'inline-block',
  },
  activa: {
    padding: '2px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    background: 'var(--success-bg)',
    color: 'var(--success)',
    display: 'inline-block',
  },
  pausada: {
    padding: '2px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    background: 'var(--warning-bg)',
    color: 'var(--warning)',
    display: 'inline-block',
  },
  completada: {
    padding: '2px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    background: 'var(--info-bg)',
    color: 'var(--info)',
    display: 'inline-block',
  },
}

// ─── Helpers ───
function getTodayStr(): string {
  return new Date().toISOString().split('T')[0]
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '-'
  const d = new Date(dateStr)
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short' })
}

function formatTime(timeStr: string): string {
  if (!timeStr) return '-'
  // timeStr = "HH:MM" or "HH:MM:SS"
  const [h, m] = timeStr.split(':')
  const hour = parseInt(h, 10)
  const ampm = hour >= 12 ? 'PM' : 'AM'
  const h12 = hour % 12 || 12
  return `${h12}:${m} ${ampm}`
}

// ─── Skeleton component ───
function SkeletonBlock({ width, height, style }: { width?: string | number; height?: string | number; style?: React.CSSProperties }) {
  return (
    <div
      style={{
        ...styles.skeleton,
        width: width || '100%',
        height: height || 20,
        ...style,
      }}
    />
  )
}

function LoadingSkeleton() {
  return (
    <div style={styles.page}>
      {/* KPI skeleton */}
      <div style={{ ...styles.header, marginBottom: 24 }}>
        <SkeletonBlock width={240} height={32} />
        <div style={{ display: 'flex', gap: 12 }}>
          <SkeletonBlock width={150} height={42} style={{ borderRadius: 8 }} />
          <SkeletonBlock width={120} height={42} style={{ borderRadius: 8 }} />
        </div>
      </div>
      <div style={styles.kpiGrid}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} style={styles.card}>
            <SkeletonBlock width={80} height={38} style={{ marginBottom: 8 }} />
            <SkeletonBlock width={120} height={14} />
          </div>
        ))}
      </div>
      {/* Funnel skeleton */}
      <div style={{ ...styles.card, marginBottom: 24 }}>
        <SkeletonBlock width={180} height={20} style={{ marginBottom: 20 }} />
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} style={{ ...styles.funnelRow }}>
            <SkeletonBlock width={90} height={14} />
            <div style={{ flex: 1 }}>
              <SkeletonBlock height={32} style={{ borderRadius: 8 }} />
            </div>
            <SkeletonBlock width={40} height={14} />
          </div>
        ))}
      </div>
      {/* Two-col skeleton */}
      <div style={styles.twoCol}>
        <div style={styles.card}>
          <SkeletonBlock width={180} height={20} style={{ marginBottom: 16 }} />
          {[1, 2, 3].map((i) => (
            <div key={i} style={{ marginBottom: 16 }}>
              <SkeletonBlock width="60%" height={14} style={{ marginBottom: 8 }} />
              <SkeletonBlock height={6} style={{ borderRadius: 3 }} />
            </div>
          ))}
        </div>
        <div style={styles.card}>
          <SkeletonBlock width={180} height={20} style={{ marginBottom: 16 }} />
          {[1, 2, 3].map((i) => (
            <div key={i} style={{ marginBottom: 16 }}>
              <SkeletonBlock width="70%" height={14} style={{ marginBottom: 6 }} />
              <SkeletonBlock width="40%" height={12} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Main component ───
export default function MarketingDashboard() {
  const navigate = useNavigate()
  const api = useApi()

  // El tablero en caché. Las campañas comparten clave con la pantalla de
  // Campañas, que lee la misma lista: entrar de una a la otra no la vuelve a
  // pedir.
  const { data: campanas = [], isLoading: cargandoCampanas, error: errorCampanas } = useCachedData(
    ['campanas'], () => api.get<CampanaEmail[]>('/api/campaigns'))
  const { data: bookings = [], isLoading: cargandoBookings, error: errorBookings } = useCachedData(
    ['marketing-bookings'], () => api.get<Agendamiento[]>(`/api/booking/agendamientos?desde=${getTodayStr()}&estado=confirmado`))
  // Aislado: si el endpoint aún no está desplegado, no rompe el tablero.
  const { data: newsletter = { suscriptores: [], total: 0 } } = useCachedData(
    ['newsletter-resumen'],
    () => api.get<{ suscriptores: NewsletterSuscriptor[]; total: number }>('/api/newsletter?limit=5')
      .catch(() => ({ suscriptores: [], total: 0 })))
  const loading = cargandoCampanas || cargandoBookings
  const err = errorCampanas || errorBookings
  const error = err ? (err instanceof Error ? err.message : 'Error desconocido') : null


  // ─── KPI calculations ───
  if (error) {
    return (
      <div style={{ ...styles.page, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
        <div style={{ fontSize: 48, opacity: 0.3 }}>!</div>
        <div style={{ color: 'var(--danger)', fontSize: 16 }}>Error cargando datos: {error}</div>
        <button style={styles.btnPrimary} onClick={() => window.location.reload()}>Reintentar</button>
      </div>
    )
  }

  if (loading) {
    return (
      <>
        <style>{skeletonKeyframes}</style>
        <LoadingSkeleton />
      </>
    )
  }

  const now = new Date()
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)

  const leadsThisMonth = campanas.reduce((sum, c) => {
    const created = new Date(c.created_at)
    return created >= monthStart ? sum + c.total_leads : sum
  }, 0)

  const totalEnviados = campanas.reduce((sum, c) => sum + c.total_enviados, 0)
  const totalAbiertos = campanas.reduce((sum, c) => sum + c.total_abiertos, 0)
  const totalClicks = campanas.reduce((sum, c) => sum + c.total_clicks, 0)
  const totalLeads = campanas.reduce((sum, c) => sum + c.total_leads, 0)
  const totalProspectos = campanas.reduce((sum, c) => sum + c.total_prospectos, 0)

  const tasaApertura = totalEnviados > 0 ? Math.round((totalAbiertos / totalEnviados) * 100) : 0

  const reunionesAgendadas = bookings.length

  // ─── Funnel data ───
  const funnelSteps = [
    { label: 'Prospectos', value: totalProspectos },
    { label: 'Enviados', value: totalEnviados },
    { label: 'Abiertos', value: totalAbiertos },
    { label: 'Clicks', value: totalClicks },
    { label: 'Leads', value: totalLeads },
  ]
  const funnelMax = Math.max(...funnelSteps.map((s) => s.value), 1)

  // ─── Active campaigns ───
  const activeCampaigns = campanas.filter((c) => c.estado === 'activa' || c.estado === 'pausada')

  // ─── KPIs ───
  const kpis = [
    { value: leadsThisMonth, label: 'Leads este mes' },
    { value: totalEnviados.toLocaleString(), label: 'Emails enviados' },
    { value: reunionesAgendadas, label: 'Reuniones agendadas' },
    { value: `${tasaApertura}%`, label: 'Tasa de apertura' },
  ]



  return (
    <>
      <style>{skeletonKeyframes}</style>
      <div style={styles.page}>
        {/* ─── Header ─── */}
        <PageHeader
          breadcrumbs={[{ label: 'Marketing' }]}
          actions={<>
            <button
              style={styles.btnPrimary}
              onClick={() => navigate('/marketing/email')}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.85' }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1' }}
            >
              Nueva Campaña
            </button>
            <button
              style={styles.btnSecondary}
              onClick={() => navigate('/marketing/agenda')}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.85' }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1' }}
            >
              Ver Agenda
            </button>
            <button
              style={{ ...styles.btnSecondary, background: 'color-mix(in srgb, var(--primary-light) 12%, transparent)', borderColor: 'color-mix(in srgb, var(--primary-light) 25%, transparent)', color: 'var(--primary-light)' }}
              onClick={() => navigate('/marketing/flujos')}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.85' }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1' }}
            >
              ⚡ Flujos
            </button>
          </>}
        />

        {/* ─── KPI Cards ─── */}
        <div style={styles.kpiGrid}>
          {kpis.map((kpi, i) => (
            <div key={i} style={styles.card}>
              <p style={styles.kpiValue}>{kpi.value}</p>
              <p style={styles.kpiLabel}>{kpi.label}</p>
            </div>
          ))}
        </div>

        {/* ─── Funnel ─── */}
        <div style={{ ...styles.card, marginBottom: 24 }}>
          <h2 style={styles.sectionTitle}>Embudo de Conversión</h2>
          {funnelSteps.map((step) => {
            const pct = (step.value / funnelMax) * 100
            return (
              <div key={step.label} style={styles.funnelRow}>
                <span style={styles.funnelLabel}>{step.label}</span>
                <div style={styles.funnelTrack}>
                  <div
                    style={{
                      ...styles.funnelBar,
                      width: `${Math.max(pct, 2)}%`,
                    }}
                  />
                </div>
                <span style={styles.funnelCount}>{step.value.toLocaleString()}</span>
              </div>
            )
          })}
        </div>

        {/* ─── Suscriptores del blog ─── */}
        <div style={{ ...styles.card, marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h2 style={{ ...styles.sectionTitle, margin: 0 }}>Suscriptores del blog</h2>
            <button
              style={{ ...styles.btnSecondary, padding: '6px 14px', fontSize: 13 }}
              onClick={() => navigate('/marketing/newsletter')}
            >
              Ver todos ({newsletter.total})
            </button>
          </div>
          {newsletter.suscriptores.length === 0 ? (
            <div style={styles.emptyState}>Aún no hay suscriptores</div>
          ) : (
            newsletter.suscriptores.map((s) => (
              <div key={s.id} style={styles.bookingItem}>
                <div>
                  <div style={styles.bookingName}>{s.email}</div>
                  <div style={styles.bookingMeta}>
                    {s.origen}{s.fuente_url ? ` · ${s.fuente_url}` : ''}
                  </div>
                </div>
                <div style={styles.bookingTime as React.CSSProperties}>
                  {formatDate(s.created_at)}
                </div>
              </div>
            ))
          )}
        </div>

        {/* ─── Two-column: Campaigns + Bookings ─── */}
        <div style={styles.twoCol}>
          {/* Active Campaigns */}
          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>Campañas Activas</h2>
            {activeCampaigns.length === 0 ? (
              <div style={styles.emptyState}>No hay campañas activas</div>
            ) : (
              activeCampaigns.map((camp) => {
                const progress = camp.total_prospectos > 0
                  ? (camp.total_enviados / camp.total_prospectos) * 100
                  : 0

                return (
                  <div key={camp.id} style={styles.campaignItem}>
                    <div style={styles.campaignHeader}>
                      <span style={styles.campaignName}>{camp.nombre}</span>
                      <span style={badgeStyles[camp.estado] || badgeStyles.borrador}>
                        {camp.estado.charAt(0).toUpperCase() + camp.estado.slice(1)}
                      </span>
                    </div>
                    <div style={styles.campaignStats}>
                      {camp.total_enviados} / {camp.total_prospectos} enviados · {camp.total_abiertos} abiertos · {camp.total_leads} leads
                    </div>
                    <div style={styles.progressTrack}>
                      <div style={{ ...styles.progressFill, width: `${Math.min(progress, 100)}%` }} />
                    </div>
                  </div>
                )
              })
            )}
          </div>

          {/* Upcoming Bookings */}
          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>Próximas Reuniones</h2>
            {bookings.length === 0 ? (
              <div style={styles.emptyState}>No hay reuniones próximas</div>
            ) : (
              bookings.slice(0, 8).map((booking) => (
                <div key={booking.id} style={styles.bookingItem}>
                  <div>
                    <div style={styles.bookingName}>{booking.nombre}</div>
                    <div style={styles.bookingMeta}>
                      {booking.empresa || booking.email}
                      {booking.origen && booking.origen !== 'directo' && ` · ${booking.origen.replace('_', ' ')}`}
                    </div>
                  </div>
                  <div style={styles.bookingTime as React.CSSProperties}>
                    {formatDate(booking.fecha)}
                    <br />
                    {formatTime(booking.hora_inicio)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  )
}
